import {
    Box,
    Grid,
    Paper,
    Typography,
    alpha,
    useTheme
} from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

const KpiCharts = ({ kpiDetails }) => {
    const theme = useTheme();
    const { goals = [] } = kpiDetails;
    const [selectedGoalId, setSelectedGoalId] = useState(null);

    // Check if any scores exist across all goals
    const hasAnyScores = () => {
        return goals.some(goal => goal.scores && goal.scores.length > 0);
    };

    // Prepare timeline data with actual dates
    const prepareTimelineData = () => {
        // Get all unique dates when scores were entered
        const allDates = new Set();
        const goalScoresByDate = {};

        goals.forEach(goal => {
            const goalName = goal.goal?.name || goal.name || 'Unknown Goal';
            if (goal.scores && goal.scores.length > 0) {
                goal.scores.forEach(score => {
                    const dateKey = score.scoreDate || dayjs(score.updatedAt || score.createdAt).format('YYYY-MM-DD');
                    allDates.add(dateKey);

                    if (!goalScoresByDate[dateKey]) {
                        goalScoresByDate[dateKey] = {};
                    }
                    goalScoresByDate[dateKey][goal.id] = {
                        score: parseFloat(score.score || 0),
                        goalName: goalName
                    };
                });
            }
        });

        // Sort dates chronologically
        const sortedDates = Array.from(allDates).sort();

        // Create data points for each date
        return sortedDates.map(dateKey => {
            const dataPoint = {
                date: dayjs(dateKey).format('MMM DD'),
                fullDate: dayjs(dateKey).format('MMM DD, YYYY')
            };

            // Add score for each goal on this date, or carry forward last known score
            goals.forEach(goal => {
                const goalId = goal.id;
                const goalName = goal.goal?.name || goal.name || 'Unknown Goal';
                const scoreData = goalScoresByDate[dateKey]?.[goalId];

                if (scoreData) {
                    // Score exists for this date
                    dataPoint[`goal_${goalId}`] = scoreData.score;
                    dataPoint[`goal_${goalId}_name`] = scoreData.goalName;
                } else {
                    // No score for this date, check if there's a previous score to carry forward
                    let lastScore = null;
                    // Find the most recent score for this goal before this date
                    for (let i = sortedDates.indexOf(dateKey) - 1; i >= 0; i--) {
                        const prevDateKey = sortedDates[i];
                        if (goalScoresByDate[prevDateKey]?.[goalId]) {
                            lastScore = goalScoresByDate[prevDateKey][goalId].score;
                            break;
                        }
                    }
                    // Only add the score if we found a previous score (carry forward)
                    // Otherwise, leave it undefined so the line doesn't start until first score
                    if (lastScore !== null) {
                        dataPoint[`goal_${goalId}`] = lastScore;
                    }
                    dataPoint[`goal_${goalId}_name`] = goalName;
                }
            });

            return dataPoint;
        });
    };

    // Prepare contribution data
    const prepareContributionData = () => {
        return goals.map(goal => {
            const scores = goal.scores || [];
            const averageScore = scores.length > 0
                ? scores.reduce((acc, score) => acc + parseFloat(score.score || 0), 0) / scores.length
                : 0;
            const weight = parseFloat(goal.weight || 0);
            const contribution = (averageScore * weight) / 5;

            return {
                name: goal.goal?.name || goal.name || 'Unknown Goal',
                shortName: (goal.goal?.name || goal.name || 'Unknown Goal').substring(0, 15) + '...',
                fullName: goal.goal?.name || goal.name || 'Unknown Goal',
                averageScore: averageScore,
                weight: weight,
                contribution: contribution
            };
        });
    };

    const timelineData = prepareTimelineData();
    const contributionData = prepareContributionData();

    // Handle legend click to highlight/unhighlight goal lines
    const handleLegendClick = (data) => {
        const clickedGoalId = data.dataKey.replace('goal_', '');
        // Toggle selection: if already selected, deselect; otherwise select
        setSelectedGoalId(prevId => prevId === clickedGoalId ? null : clickedGoalId);
    };

    // Custom tooltip to show full goal name
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;

            // Check if this is contribution chart (has weight and contribution)
            if (data.weight !== undefined && data.contribution !== undefined) {
                return (
                    <Paper elevation={3} sx={{ p: 2, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, maxWidth: 300 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                            {data.fullName}
                        </Typography>
                        {payload.map((entry, index) => (
                            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="body2" sx={{ mr: 2 }}>
                                    {entry.name}:
                                </Typography>
                                <Typography variant="body2" fontWeight={600} sx={{ color: entry.color }}>
                                    {entry.value.toFixed(1)}
                                </Typography>
                            </Box>
                        ))}
                        {data.averageScore && (
                            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                                Avg Score: {data.averageScore.toFixed(1)}/5
                            </Typography>
                        )}
                    </Paper>
                );
            }

            // Timeline chart tooltip
            return (
                <Paper elevation={3} sx={{ p: 2, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, maxWidth: 300 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        {data.fullDate || data.date}
                    </Typography>
                    {payload.map((entry, index) => {
                        const goalId = entry.dataKey.replace('goal_', '');
                        const goalName = data[`goal_${goalId}_name`] || `Goal ${goalId}`;
                        return (
                            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography variant="body2" sx={{ mr: 2, flex: 1 }}>
                                    {goalName.length > 25 ? goalName.substring(0, 25) + '...' : goalName}:
                                </Typography>
                                <Typography variant="body2" fontWeight={600} sx={{ color: entry.color }}>
                                    {entry.value.toFixed(1)}/5
                                </Typography>
                            </Box>
                        );
                    })}
                </Paper>
            );
        }
        return null;
    };

    return (
        <Grid container spacing={3}>
            {/* Individual Goal Score Timelines */}
            <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 3, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        Individual Goal Score Timeline
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Track individual goal scores over time - each line represents a goal's performance
                    </Typography>
                    <Box sx={{ width: '100%', height: 400 }}>
                        {timelineData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={timelineData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 12 }}
                                        stroke={theme.palette.text.secondary}
                                    />
                                    <YAxis
                                        domain={[0, 5]}
                                        tick={{ fontSize: 12 }}
                                        stroke={theme.palette.text.secondary}
                                        label={{ value: 'Score', angle: -90, position: 'insideLeft' }}
                                        ticks={[0, 1, 2, 3, 4, 5]}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend onClick={handleLegendClick} wrapperStyle={{ cursor: 'pointer', color: theme.palette.text.primary }} />
                                    {goals.map((goal, index) => {
                                        const goalId = goal.id;
                                        const goalName = goal.goal?.name || goal.name || 'Unknown Goal';
                                        const colors = [theme.palette.primary.main, theme.palette.success.main, theme.palette.warning.main, theme.palette.error.main, theme.palette.secondary.main, theme.palette.info.main, theme.palette.success.dark, theme.palette.warning.dark, theme.palette.error.dark, theme.palette.text.disabled];
                                        const color = colors[index % colors.length];
                                        
                                        // Determine opacity based on selection
                                        const opacity = selectedGoalId === null 
                                            ? 1 
                                            : (selectedGoalId === goalId.toString() ? 1 : 0.1);

                                        return (
                                            <Line
                                                key={goalId}
                                                type="monotone"
                                                dataKey={`goal_${goalId}`}
                                                stroke={color}
                                                strokeWidth={2}
                                                strokeOpacity={opacity}
                                                dot={{ r: 4, fill: color, fillOpacity: opacity }}
                                                activeDot={{ r: 6 }}
                                                connectNulls={false}
                                                name={goalName.length > 20 ? goalName.substring(0, 20) + '...' : goalName}
                                            />
                                        );
                                    })}
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                <Typography variant="body2" color="text.secondary">
                                    {hasAnyScores() ? 'No score data available' : 'Scoring is not started'}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Grid>

            {/* Goal Contribution to Overall Score */}
            <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 3, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        Goal Contribution to Overall Score
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Total weight vs current contribution for each goal
                    </Typography>
                    <Box sx={{ width: '100%', height: 400 }}>
                        {contributionData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={contributionData}
                                    margin={{ top: 5, right: 30, left: 20, bottom: 20 }}
                                    cursor={{ fill: alpha(theme.palette.divider, 0.15) }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                    <XAxis
                                        dataKey="shortName"
                                        angle={0}
                                        textAnchor="middle"
                                        height={60}
                                        interval={0}
                                        tick={false}
                                        stroke={theme.palette.text.secondary}
                                    />
                                    <YAxis
                                        domain={[0, 5]}
                                        tick={{ fontSize: 12 }}
                                        stroke={theme.palette.text.secondary}
                                        ticks={[0, 1, 2, 3, 4, 5]}
                                        label={{ value: 'Weight', angle: -90, position: 'insideLeft' }}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ color: theme.palette.text.primary }} />
                                    <Bar dataKey="weight" fill={theme.palette.primary.main} name="Total Weight" radius={[4, 4, 0, 0]} activeBar={{ fill: alpha(theme.palette.primary.main, 0.7), stroke: theme.palette.primary.main, strokeWidth: 1 }} />
                                    <Bar dataKey="contribution" fill={theme.palette.success.main} name="Current Contribution" radius={[4, 4, 0, 0]} activeBar={{ fill: alpha(theme.palette.success.main, 0.7), stroke: theme.palette.success.main, strokeWidth: 1 }} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                <Typography variant="body2" color="text.secondary">
                                    {hasAnyScores() ? 'No goal data available' : 'Scoring is not started'}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default KpiCharts;